import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { CopyIcon, DownloadIcon, InfoIcon } from "@chakra-ui/icons";
import {
	Badge,
	Box,
	Button,
	Flex,
	FormControl,
	FormLabel,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Select,
	Stack,
	Text,
	Textarea,
	Tooltip,
	useDisclosure,
	useToast,
} from "@chakra-ui/react";
import moment from "moment";

import Dropzone from "../../Components/Dropzone/Dropzone";
import FinancialStatusTable from "../../Components/FinancialStatus/FinancialStatusTable";
import FinancialStatusWarnings from "../../Components/FinancialStatus/FinancialStatusWarnings";
import WithConfirmation from "../../Components/WithConfirmation/WithConfirmation";
import { updateFinancialStatusAsync } from "../../redux/user/userSlice";
import { _arrayBufferToBase64 } from "../../utils/convertors";
import {
	buildYearBreakdown,
	formatAmount,
	getActiveAdministrationContracts,
	getFinalizeMonthDefaults,
	getSavedMonthEntry,
	getTotalAmountByStatus,
} from "../../utils/financialStatus";
import { API } from "../../utils/http";
import { fullDateFormat } from "../../utils/types";
import { finalizeMonthVerifier } from "../../utils/verifier";

export default function FinancialStatus({ owner, properties = [] }) {
	const { t } = useTranslation();
	const toast = useToast();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const months = moment.months();
	const [year, setYear] = useState(moment().year());
	const [month, setMonth] = useState();
	const [error, setError] = useState({});
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isExportModalOpen, onOpen: onExportModalOpen, onClose: onExportModalClose } = useDisclosure();

	const financialStatus = owner?.financialStatus;
	const ownerCurrency = owner?.bankDetails?.currency;
	const ownerEmail = owner?.email;

	const initialFinalizeMonthInformation = {
		year: "",
		month: "",
		status: "",
		ownerEmail,
		amount: "",
		document: {},
		date: moment().format("yyyy-MM-DD"),
		currency: "",
		description: "",
	};
	const [finalizeMonthInformation, setFinalizeMonthInformation] = useState(initialFinalizeMonthInformation);

	const yearBreakdown = useMemo(
		() =>
			buildYearBreakdown({
				properties,
				year,
				ownerCurrency,
				financialStatus,
			}),
		[properties, year, ownerCurrency, financialStatus],
	);

	const activeAdministrationContracts = useMemo(() => getActiveAdministrationContracts(properties), [properties]);
	const savedMonth = getSavedMonthEntry(financialStatus, year, month);

	const goToProperty = (id) => {
		navigate(`/properties/view/${id}?tab=payments`);
	};

	const changeMonthStatus = () => {
		const errors = finalizeMonthVerifier(finalizeMonthInformation);
		setError(errors);
		if (Object.keys(errors).length > 0) {
			toast({
				title: "Error encountered",
				description: "Invalid information",
				status: "error",
				duration: 9000,
				isClosable: true,
			});
			return;
		}
		dispatch(updateFinancialStatusAsync(finalizeMonthInformation)).finally(() => {
			onClose();
			setFinalizeMonthInformation(initialFinalizeMonthInformation);
		});
	};

	const resetMonthStatus = () => {
		const payload = {
			year: finalizeMonthInformation.year,
			month: finalizeMonthInformation.month,
			ownerEmail,
			status: "reset",
		};
		dispatch(updateFinancialStatusAsync(payload)).finally(() => {
			onClose();
			setFinalizeMonthInformation(initialFinalizeMonthInformation);
		});
	};

	const onDocumentDrop = async (acceptedFiles) => {
		if (!acceptedFiles?.[0]) return;
		const buffer = await acceptedFiles[0].arrayBuffer();
		const documentName = acceptedFiles[0].name;
		const contentType = acceptedFiles[0].type;
		setFinalizeMonthInformation({
			...finalizeMonthInformation,
			document: { documentData: _arrayBufferToBase64(buffer), documentName, contentType },
		});
	};

	const handleGetDocument = (documentId, documentName) => {
		if (!documentId) return;
		API.put("get-owner-financial-status-url", { documentId, documentName, ownerEmail })
			.then((r) => {
				if (r.data?.url) window.open(r.data.url, "_blank");
			})
			.catch((e) => {
				toast({
					title: "Error encountered",
					description: e.response?.data?.error?.message || "Failed to get document",
					status: "error",
					duration: 9000,
					isClosable: true,
				});
			});
	};

	const handleExportFinancialStatus = async (format = "pdf") => {
		try {
			const response = await API.put("export-financial-status", {
				ownerEmail,
				outputFormat: format,
			});
			if (!response.data || !response.data.output) {
				toast({
					title: "Error",
					description: "Failed to export financial status",
					status: "error",
				});
				return;
			}
			const mimeType = format === "pdf" ? "application/pdf" : "text/csv";
			const link = document.createElement("a");
			link.href = `data:${mimeType};base64,${response.data.output}`;
			link.download = `financial-status-${owner?.name}-${moment().format("DD-MM-YYYY")}.${format}`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} catch (exportError) {
			toast({
				title: "Error",
				description: exportError.response?.data?.error?.message || "Failed to export financial status",
				status: "error",
				duration: 5000,
				isClosable: true,
			});
		}
	};

	const openFinalizeMonth = (monthIndex, liveNet) => {
		setMonth(monthIndex);
		setFinalizeMonthInformation(
			getFinalizeMonthDefaults({
				financialStatus,
				year,
				monthIndex,
				ownerCurrency,
				liveNet,
				ownerEmail,
				defaultDate: initialFinalizeMonthInformation.date,
			}),
		);
		onOpen();
	};

	const renderChangeMonthStatusModal = () => (
		<Modal isOpen={isOpen} onClose={onClose} size={"xl"}>
			<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
			<ModalContent>
				<ModalHeader>
					<Text>Finalize month {months[month]}</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Stack>
						<FormControl display={"flex"} alignItems="center">
							<FormLabel display={"inline"}>Month Status:</FormLabel>
							<Select
								isInvalid={error?.status}
								width={"fit-content"}
								onChange={(e) => {
									setFinalizeMonthInformation({ ...finalizeMonthInformation, status: e.target.value });
								}}
								value={finalizeMonthInformation?.status}
								placeholder="Month Status"
								_placeholder={{ color: "gray.500" }}
							>
								<option value={"custodie"}>Custodie</option>
								<option value={"transferat"}>Transfer Bancar</option>
								<option value={"predareCash"}>Predare Cash</option>
								<option value={"ownerAccount"}>Cont Proprietar</option>
								<option value={"transferCustodie"}>Transfer Bancar din custodie</option>
								<option value={"predareCashCustodie"}>Predare Cash din custodie</option>
							</Select>
						</FormControl>
						<FormControl>
							<FormLabel display={"inline"}>Description:</FormLabel>
							<Textarea
								name="description"
								placeholder="Description"
								_placeholder={{ color: "gray.500" }}
								value={finalizeMonthInformation.description}
								onChange={(e) => {
									setFinalizeMonthInformation({ ...finalizeMonthInformation, description: e.target.value });
								}}
							/>
						</FormControl>
						<FormControl>
							<FormLabel display={"inline"}>Amount:</FormLabel>
							<Input
								width="fit-content"
								type="number"
								name="amount"
								placeholder="Amount"
								_placeholder={{ color: "gray.500" }}
								value={finalizeMonthInformation.amount}
								onChange={(e) => {
									setFinalizeMonthInformation({ ...finalizeMonthInformation, amount: e.target.value });
								}}
							/>
						</FormControl>
						<Box>
							<Text as="span">Currency: </Text>
							<Text as="span">{finalizeMonthInformation.currency} </Text>
						</Box>
						<FormControl>
							<FormLabel display={"inline"}>Date:</FormLabel>
							<Input
								width={"fit-content"}
								onChange={(e) => {
									setFinalizeMonthInformation({ ...finalizeMonthInformation, date: e.target.value });
								}}
								type="date"
								name="date"
								value={finalizeMonthInformation.date}
								placeholder="Type your message here..."
								_placeholder={{ color: "gray.500" }}
							/>
						</FormControl>
						<Box hidden={!finalizeMonthInformation.document?.documentName}>
							<Text as="span">Document: </Text>
							<Text
								color="teal.500"
								textDecorationLine={"underline"}
								cursor={"pointer"}
								onClick={() => {
									handleGetDocument(finalizeMonthInformation.document.id, finalizeMonthInformation.document.documentName);
								}}
							>
								{finalizeMonthInformation.document?.documentName}
								<DownloadIcon marginLeft={2} />
							</Text>
						</Box>
						<FormControl>
							<FormLabel display={"inline"}>Payment Proof:</FormLabel>
							<Dropzone
								onDrop={onDocumentDrop}
								accept={{
									"application/pdf": [".pdf"],
									"application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
									"application/msword": [".doc"],
								}}
								description="Upload PDF,DOC or DOCX Format"
							/>
						</FormControl>
					</Stack>
				</ModalBody>
				<ModalFooter justifyContent={"space-between"}>
					<Stack direction="row" alignItems={"center"} spacing={0}>
						<WithConfirmation onConfirm={resetMonthStatus}>
							<Button colorScheme="red" mr={3} isDisabled={!savedMonth}>
								Reset
							</Button>
						</WithConfirmation>
						<Tooltip label="Reseteaza status-ul lunii curente">
							<Box as={InfoIcon} cursor="pointer" _hover={{ color: "blue.500" }} />
						</Tooltip>
					</Stack>
					<Box>
						<Button colorScheme="red" onClick={onClose} mr={3}>
							Cancel
						</Button>
						<Button colorScheme="blue" onClick={changeMonthStatus}>
							Save
						</Button>
					</Box>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);

	const renderExportModal = () => (
		<Modal isOpen={isExportModalOpen} onClose={onExportModalClose} size="md">
			<ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
			<ModalContent>
				<ModalHeader>
					<Text>Export Financial Status</Text>
				</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Stack spacing={4}>
						<Stack direction="row" spacing={4}>
							<Button
								leftIcon={<DownloadIcon />}
								colorScheme="blue"
								onClick={() => {
									handleExportFinancialStatus("pdf");
									onExportModalClose();
								}}
								flex={1}
							>
								Export PDF
							</Button>
							<Button
								leftIcon={<DownloadIcon />}
								colorScheme="blue"
								onClick={() => {
									handleExportFinancialStatus("csv");
									onExportModalClose();
								}}
								flex={1}
							>
								Export CSV
							</Button>
						</Stack>
					</Stack>
				</ModalBody>
				<ModalFooter>
					<Button colorScheme="red" onClick={onExportModalClose}>
						Cancel
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);

	return (
		<Stack gap={3}>
			{renderChangeMonthStatusModal()}
			{renderExportModal()}

			<Flex justifyContent="space-between" alignItems="flex-start" gap={4} flexWrap="wrap">
				<Stack spacing={1}>
					{activeAdministrationContracts.length === 0 ? (
						<Text fontWeight={"bold"}>Nu exista contracte de administrare active.</Text>
					) : (
						activeAdministrationContracts.map((a, index) => (
							<Stack direction="row" alignItems="center" key={a.number || index} fontSize="sm">
								<Text>Contract activ de administrare nr. </Text>
								<Text fontWeight={"bold"}>
									{a.number}{" "}
									<Tooltip label="Copy number">
										<CopyIcon cursor={"pointer"} onClick={() => navigator.clipboard.writeText(a.number)} />
									</Tooltip>
								</Text>
								<Text>din</Text>
								<Text fontWeight={"bold"}>{a.startingDate ? moment(a.startingDate).format(fullDateFormat) : "-"}</Text>
							</Stack>
						))
					)}
					<Box pt={1}>
						<Badge fontSize="sm">
							Total custodie: {formatAmount(getTotalAmountByStatus(financialStatus, "custodie"))} {ownerCurrency?.toUpperCase()}
						</Badge>
					</Box>
				</Stack>
				<Button colorScheme="blue" leftIcon={<DownloadIcon />} onClick={onExportModalOpen}>
					{t("financialStatus.exportFinancialStatus")}
				</Button>
			</Flex>

			<FinancialStatusWarnings warnings={yearBreakdown.warnings} />

			<Flex direction="row" alignItems="center" justifyContent="center" gap={4}>
				<Button size="sm" onClick={() => setYear(year - 1)}>
					Prev year
				</Button>
				<Text fontWeight="bold" fontSize="lg" minW="4rem" textAlign="center">
					{year}
				</Text>
				<Button size="sm" isDisabled={year === moment().year()} onClick={() => setYear(year + 1)}>
					Next year
				</Button>
			</Flex>

			<FinancialStatusTable
				breakdown={yearBreakdown}
				year={year}
				ownerCurrency={ownerCurrency}
				financialStatus={financialStatus}
				onFinalizeMonth={openFinalizeMonth}
				onPropertyClick={goToProperty}
			/>
		</Stack>
	);
}
